"use client";

import { useCallback, useEffect } from "react";

import { mockAuthService } from "../services/mock-auth.service";
import { apiAuthService } from "../services/auth.api";
import { useAuthStore } from "../stores/auth.store";
import { isMockDataEnabled } from "@/configs/environment.config";
import type { LoginFormValues } from "../schemas/login.schema";

export function useAuth() {
  const { isAuthenticated, user, permissions, setAuthenticated, setUnauthenticated, setHydrated } = useAuthStore();

  const login = useCallback(async (values: LoginFormValues) => {
    const service = isMockDataEnabled() ? mockAuthService : apiAuthService;
    const nextUser = await service.login(values);
    setAuthenticated(nextUser, nextUser.permissions);
  }, [setAuthenticated]);

  const logout = useCallback(async () => {
    const service = isMockDataEnabled() ? mockAuthService : apiAuthService;
    await service.logout();
    setUnauthenticated();
  }, [setUnauthenticated]);

  const hydrate = useCallback(async () => {
    const service = isMockDataEnabled() ? mockAuthService : apiAuthService;
    const currentUser = await service.getCurrentUser();
    if (currentUser) {
      setAuthenticated(currentUser, currentUser.permissions);
    } else {
      setUnauthenticated();
    }
    // Debug: check token existence without logging the token value
    if (typeof window !== "undefined") {
      const token = window.localStorage.getItem("access_token");
      // eslint-disable-next-line no-console
      console.log("[auth debug] token exists:", !!token, "length:", token ? token.length : 0);
    }
    setHydrated(true);
  }, [setAuthenticated, setHydrated, setUnauthenticated]);

  useEffect(() => {
    void hydrate();
  }, [hydrate]);

  return {
    user,
    permissions,
    isAuthenticated,
    isHydrated: useAuthStore((state) => state.isHydrated),
    login,
    logout,
    hydrate,
  };
}
